import { createApp } from 'vue'
import App from './App.vue'
import { router } from '@/router'
import { pinia } from '@/plugins/pinia'
import { vuetify } from '@/plugins/vuetify'
import { registerBaseComponents } from '@/plugins/global-components'
import { createTourPlugin } from '@/features/tour'
import { setupProviders } from './providers'
import { initTelemetry } from '@/infrastructure/analytics'
import { installErrorHandlers, installDialogFocusReturn } from '@/composables'
import { loadRuntimeConfig } from '@/config/runtime-config'

import '@fontsource-variable/geist'
import '@fontsource-variable/geist-mono'
import '@/styles/main.scss'

async function bootstrap(): Promise<void> {
  // Environment config (FO API URL, reCAPTCHA site key, App Insights connection
  // string, AI API origin) is served by the backend rather than baked in by Vite.
  // Awaited before mount so the first component setup already sees real values.
  // Never rejects — on failure it leaves safe defaults in place.
  await loadRuntimeConfig()

  // Fire-and-forget: dynamically loads the App Insights SDK when a connection string
  // is configured, and no-ops otherwise. trackException stays a safe no-op until ready.
  initTelemetry()

  const app = createApp(App)

  app.use(pinia)
  app.use(router)
  app.use(vuetify)

  registerBaseComponents(app)
  app.use(createTourPlugin(router))
  setupProviders(app)
  installErrorHandlers(app)
  // Accessibility: return focus to the element that opened a dialog once it closes, instead of
  // letting focus fall through to <body> (Vuetify only restores focus for activator-opened dialogs).
  installDialogFocusReturn()

  // Wait for the initial navigation to resolve before mounting so the first component setup
  // already sees the real route (not the START_LOCATION "/"). Without this, route-derived
  // initial state — e.g. the sidebar auto-expanding the group that owns the current page — is
  // computed against "/" and only corrects after mount, which Vuetify's list open-state misses.
  await router.isReady()

  app.mount('#app')
}

void bootstrap()
